import { objectEntries, type ReadonlyRecord } from '../../utils'

import { type ColumnView, defaultColumnView } from '../../domain'

import type { ColumnViewPersistenceService } from '../../application'

import { BaseWebStorage } from './Base'
import { isAllSerializedColumnViews } from './Validators'

type SerializedColumnLists = [
    /** @see {ColumnView['order']} */
    order: string[],
    /** @see {ColumnView['hidden']} */
    hidden: string[],
    /** @see {ColumnView['pinned']} */
    pinned: string[],
]

type SerializedSizedColumnView = [
    /** @see {ColumnView['order']} */
    order: string[],
    /** @see {ColumnView['hidden']} */
    hidden: string[],
    /** @see {ColumnView['pinned']} */
    pinned: string[],
    /** @see {ColumnView['widths']} */
    widths: Record<string, number>,
]

/** The widths are left out when no column was sized, as in views stored before columns could be sized */
type SerializedColumnView = SerializedColumnLists | SerializedSizedColumnView

type AllPersistedColumnViews = ReadonlyRecord<string, ColumnView>
export type AllSerializedColumnViews = ReadonlyRecord<string, SerializedColumnView>

export class WebStorageColumnViewPersistenceService extends BaseWebStorage<AllPersistedColumnViews, AllSerializedColumnViews>
    implements ColumnViewPersistenceService
{
    protected readonly fallbackValue = {}

    protected readonly key = 'cwTableColumns'

    /** A view that outlived a column is still valid, as names that aren't columns any more are ignored */
    protected override readonly ignoreVersionMismatch = true

    protected toSerializable (views: AllPersistedColumnViews): AllSerializedColumnViews {
        return objectEntries(views).reduce<AllSerializedColumnViews>((r, [id, { order, hidden, pinned, widths }]) => {
            /** JSON writes NaN and Infinity as null, which the validator rejects, and that would lose every table's view on the next read */
            const finiteWidths = Object.fromEntries(Object.entries(widths).filter(([, width]) => Number.isFinite(width)))
            const serialized: SerializedColumnView = Object.keys(finiteWidths).length === 0
                ? [[...order], [...hidden], [...pinned]]
                : [[...order], [...hidden], [...pinned], finiteWidths]
            return { ...r, [id]: serialized }
        }, {})
    }

    protected isValidSerializable (unknownValue: unknown): unknownValue is AllSerializedColumnViews {
        return isAllSerializedColumnViews(unknownValue)
    }

    protected fromSerializable (serialized: AllSerializedColumnViews): AllPersistedColumnViews {
        return objectEntries(serialized).reduce<AllPersistedColumnViews>((r, [id, view]) => {
            const [order, hidden, pinned] = view
            return { ...r, [id]: { order, hidden, pinned, widths: view.length === 4 ? view[3] : {} } }
        }, {})
    }

    retrieve (id: string): ColumnView {
        return this.retrieveDeserialize()[id] ?? defaultColumnView
    }

    persist (id: string, view: ColumnView): void {
        this.persistSerialize({ ...this.retrieveDeserialize(), [id]: view })
    }

    drop (id: string): void {
        const { [id]: _removed, ...views } = this.retrieveDeserialize()
        this.persistSerialize(views)
    }

    flush (): void {
        this.remove()
    }
}
